import { DateTime } from 'luxon';
import type { ComponentProps } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import Avatar from '@/components/Custom/Avatar';
import DateTimePicker from '@/components/Custom/DateTimePicker';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';
import TextInput from '@/components/UI/TextInput';
import Icon from '@/components/Unistyles/Icon';
import Pressable from '@/components/Unistyles/Pressable';
import { MAXIMUM_EVENT_TITLE_LENGTH } from '@/utils/constants';
import type {
  GeneratedGame,
  GeneratedGameTeam,
  GeneratedGameTeamSlot,
  GeneratedGameTeamSlotSelection,
} from './GeneratedGame.types';
import { getGeneratedGameTeamIds } from './GeneratedGame.utils';

type GeneratedGameCardProps = {
  datetimeTestID?: string;
  game: GeneratedGame;
  isTeamEditing?: boolean;
  locationTestID?: string;
  onChangeDateTime?: (value: string | null) => void;
  onChangeLocation?: (value: string) => void;
  onChangeTitle?: (value: string) => void;
  onPressDelete?: () => void;
  onPressSwapTeams?: () => void;
  onPressTeamSlot?: (selection: GeneratedGameTeamSlotSelection) => void;
  selectedRosterTeamId?: string | null;
  selectedSlot?: GeneratedGameTeamSlotSelection | null;
  teams: readonly GeneratedGameTeam[];
  titleTestID?: string;
};

type GeneratedGameCardIconButtonProps = {
  accessibilityLabel: string;
  icon: ComponentProps<typeof Icon>['name'];
  iconVariant?: ComponentProps<typeof Icon>['variant'];
  onPress: () => void;
  testID: string;
};

function getTeamById(
  teams: readonly GeneratedGameTeam[],
  teamId: string | null | undefined,
) {
  if (!teamId) {
    return null;
  }

  return teams.find((team) => team.id === teamId) ?? null;
}

function GeneratedGameTime({ datetime }: { datetime?: string | null }) {
  const date = DateTime.fromISO(datetime ?? '');
  const isValid = date.isValid;

  return (
    <Box center style={styles.timeBlock}>
      <Text style={styles.timeText} variant="typographySecondary" size="sm">
        {isValid ? date.toFormat('EEE').toUpperCase() : 'DATE'}
      </Text>
      <Text style={styles.dateText} size="md">
        {isValid ? date.toFormat('LLL d').toUpperCase() : 'TBD'}
      </Text>
      <Text style={styles.timeText} variant="typographySecondary" size="sm">
        {isValid ? date.toFormat('h:mm a') : 'TIME'}
      </Text>
    </Box>
  );
}

function GeneratedGameInfo({ game }: { game: GeneratedGame }) {
  return (
    <Box flex style={styles.info} space="xs">
      <Text bold size="lg" numberOfLines={1}>
        {game.title || 'Game'}
      </Text>
      {game.location ? (
        <Box direction="horizontal" alignItems="center" space="sm">
          <Icon
            name="location-outline"
            size="sm"
            variant="typographySecondary"
          />
          <Text
            flexShrink={1}
            numberOfLines={1}
            size="sm"
            variant="typographySecondary"
          >
            {game.location}
          </Text>
        </Box>
      ) : null}
    </Box>
  );
}

function GeneratedGameCardIconButton({
  accessibilityLabel,
  icon,
  iconVariant = 'typographySecondary',
  onPress,
  testID,
}: GeneratedGameCardIconButtonProps) {
  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      onPress={onPress}
      styleOverride={({ pressed }) => styles.cardIconButton(pressed)}
      testID={testID}
    >
      <Icon name={icon} size="xl" variant={iconVariant} />
    </Pressable>
  );
}

function GeneratedGameTeamRow({
  isEditing,
  onPress,
  selected,
  slot,
  targeted,
  team,
}: {
  isEditing?: boolean;
  onPress?: () => void;
  selected?: boolean;
  slot: GeneratedGameTeamSlot;
  targeted?: boolean;
  team: GeneratedGameTeam | null;
}) {
  const teamAvatar = (
    <Avatar
      color={team?.home_primary_color}
      id={team?.avatar_id ?? ''}
      name={team?.title ?? ''}
      size="lg"
    />
  );
  const teamTextAlign = isEditing ? 'left' : undefined;
  const teamText = (
    <Box flex space="xs" style={styles.teamText(isEditing)}>
      <Text
        size="xs"
        bold
        variant="typographySecondary"
        textAlign={teamTextAlign}
      >
        {slot === 'away' ? 'Away' : 'Home'}
      </Text>
      <Text
        flexShrink={1}
        numberOfLines={2}
        size="xl"
        textAlign={teamTextAlign}
      >
        {team?.title ?? 'Team'}
      </Text>
    </Box>
  );
  const content = (
    <Box
      direction="horizontal"
      alignItems="center"
      space="lg"
      style={styles.teamRowContent}
    >
      {teamAvatar}
      {teamText}
    </Box>
  );

  if (isEditing && onPress) {
    return (
      <Pressable
        accessibilityLabel={`Change ${slot} team`}
        accessibilityRole="button"
        accessibilityState={{ selected }}
        onPress={onPress}
        styleOverride={({ pressed }) =>
          styles.teamPressable({ pressed, selected, targeted })
        }
        testID={`GeneratedGame${slot === 'away' ? 'Away' : 'Home'}TeamButton`}
      >
        {content}
      </Pressable>
    );
  }

  return <Box style={styles.teamStatic}>{content}</Box>;
}

export default function GeneratedGameCard({
  datetimeTestID,
  game,
  isTeamEditing = false,
  locationTestID,
  onChangeDateTime,
  onChangeLocation,
  onChangeTitle,
  onPressDelete,
  onPressSwapTeams,
  onPressTeamSlot,
  selectedRosterTeamId,
  selectedSlot,
  teams,
  titleTestID,
}: GeneratedGameCardProps) {
  const { awayTeamId, homeTeamId } = getGeneratedGameTeamIds(game);
  const awayTeam = getTeamById(teams, awayTeamId);
  const homeTeam = getTeamById(teams, homeTeamId);
  const isEditable =
    isTeamEditing &&
    Boolean(onChangeDateTime || onChangeLocation || onChangeTitle);
  const selectedSlotMatchesGame = selectedSlot?.gameId === game.id;
  const hasSelectedRosterTeam = Boolean(selectedRosterTeamId);
  const shouldTargetTeamSlot = isTeamEditing && hasSelectedRosterTeam;

  styles.useVariants({
    editing: isTeamEditing,
  });

  const previewColumn = (
    <Box flex style={styles.previewColumn}>
      {isEditable ? (
        <Box style={styles.editor}>
          <Box style={styles.editorControls} space="sm">
            {onChangeTitle ? (
              <TextInput
                autoCapitalize="words"
                enterKeyHint="done"
                maxLength={MAXIMUM_EVENT_TITLE_LENGTH}
                placeholder="Game Title"
                testID={titleTestID}
                value={game.title ?? ''}
                onChangeText={onChangeTitle}
                containerStyle={styles.textInput}
              />
            ) : null}
            {onChangeDateTime ? (
              <DateTimePicker
                value={game.datetime}
                onChange={onChangeDateTime}
                testID={datetimeTestID}
                style={styles.dateTimeInput}
              />
            ) : null}
            {onChangeLocation ? (
              <TextInput
                enterKeyHint="done"
                placeholder="Location, optional"
                value={game.location ?? ''}
                onChangeText={onChangeLocation}
                testID={locationTestID}
                containerStyle={styles.textInput}
              />
            ) : null}
          </Box>
        </Box>
      ) : (
        <Box direction="horizontal" style={styles.header}>
          <GeneratedGameTime datetime={game.datetime} />
          <GeneratedGameInfo game={game} />
        </Box>
      )}
    </Box>
  );

  const teamRows = (
    <>
      <GeneratedGameTeamRow
        isEditing={isTeamEditing}
        onPress={() =>
          onPressTeamSlot?.({
            gameId: game.id,
            slot: 'away',
            teamId: awayTeamId,
          })
        }
        selected={selectedSlotMatchesGame && selectedSlot?.slot === 'away'}
        slot="away"
        targeted={shouldTargetTeamSlot}
        team={awayTeam}
      />
      <GeneratedGameTeamRow
        isEditing={isTeamEditing}
        onPress={() =>
          onPressTeamSlot?.({
            gameId: game.id,
            slot: 'home',
            teamId: homeTeamId,
          })
        }
        selected={selectedSlotMatchesGame && selectedSlot?.slot === 'home'}
        slot="home"
        targeted={shouldTargetTeamSlot}
        team={homeTeam}
      />
    </>
  );

  const teamColumn = (
    <Box style={styles.content} space="sm">
      {isTeamEditing ? (
        <Box direction="horizontal" alignItems="center" style={styles.teamEdit}>
          {onPressSwapTeams ? (
            <Box center style={styles.teamSwapRail}>
              <GeneratedGameCardIconButton
                accessibilityLabel="Swap home and away teams"
                icon="swap-vertical-outline"
                onPress={onPressSwapTeams}
                testID="GeneratedGameSwapTeamsButton"
              />
            </Box>
          ) : null}
          <Box flex space="sm">
            {teamRows}
          </Box>
        </Box>
      ) : (
        teamRows
      )}
    </Box>
  );

  const deleteColumn =
    isTeamEditing && onPressDelete ? (
      <Box center style={styles.deleteColumn}>
        <GeneratedGameCardIconButton
          accessibilityLabel="Delete game"
          icon="trash-outline"
          onPress={onPressDelete}
          testID="GeneratedGameDeleteButton"
        />
      </Box>
    ) : null;

  return (
    <Box style={styles.card} testID="GeneratedGameCard">
      <Box style={styles.body}>
        {isTeamEditing ? (
          <>
            {teamColumn}
            {previewColumn}
            {deleteColumn}
          </>
        ) : (
          <>
            {previewColumn}
            {teamColumn}
          </>
        )}
      </Box>
    </Box>
  );
}

const styles = StyleSheet.create((theme) => ({
  card: {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.borderMuted,
    borderCurve: 'continuous',
    borderRadius: theme.borderRadius.xl,
    borderWidth: theme.size(0.5),
    marginHorizontal: theme.size(1),
    marginVertical: theme.size(1),
    overflow: 'hidden',
  },
  body: {
    alignItems: 'stretch',
    flexDirection: 'column',
    position: 'relative',
    variants: {
      editing: {
        true: {
          flexDirection: {
            base: 'column',
            xl: 'row',
          },
          paddingRight: {
            base: theme.size(14),
            xl: 0,
          },
        },
      },
    },
  },
  content: {
    justifyContent: 'center',
    paddingBottom: theme.space('md'),
    paddingTop: theme.space('sm'),
    variants: {
      editing: {
        true: {
          minWidth: {
            base: undefined,
            xl: theme.size(66),
          },
          paddingBottom: theme.space('md'),
          paddingTop: theme.space('md'),
        },
      },
    },
  },
  deleteColumn: {
    alignSelf: {
      base: undefined,
      xl: 'stretch',
    },
    bottom: {
      base: 0,
      xl: undefined,
    },
    flexShrink: 0,
    justifyContent: 'center',
    paddingHorizontal: theme.space('lg'),
    position: {
      base: 'absolute',
      xl: 'relative',
    },
    right: {
      base: 0,
      xl: undefined,
    },
    top: {
      base: 0,
      xl: undefined,
    },
  },
  dateText: {
    fontWeight: theme.fontWeight.bold,
  },
  editor: {
    alignItems: {
      base: 'flex-start',
      xl: 'flex-end',
    },
    justifyContent: 'center',
    padding: theme.space('md'),
  },
  editorControls: {
    alignItems: 'stretch',
  },
  header: {
    alignItems: 'stretch',
  },
  info: {
    justifyContent: 'center',
    minWidth: theme.size(2),
    paddingHorizontal: theme.space('lg'),
    paddingVertical: theme.space('md'),
  },
  previewColumn: {
    minWidth: theme.size(2),
    variants: {
      editing: {
        true: {
          justifyContent: 'center',
        },
      },
    },
  },
  cardIconButton: (pressed: boolean) => ({
    alignItems: 'center',
    backgroundColor: pressed ? theme.colors.surfaceSecondary : 'transparent',
    borderRadius: theme.borderRadius.xl,
    height: theme.size(10),
    justifyContent: 'center',
    width: theme.size(10),
    _web: {
      _hover: {
        backgroundColor: theme.colors.surfaceSecondary,
      },
    },
  }),
  textInput: {
    width: '100%',
  },
  dateTimeInput: {
    alignSelf: {
      base: 'flex-start',
      xl: 'flex-end',
    },
    width: 'auto',
  },
  teamPressable: ({
    pressed,
    selected,
    targeted,
  }: {
    pressed: boolean;
    selected?: boolean;
    targeted?: boolean;
  }) => ({
    backgroundColor:
      selected || pressed ? theme.colors.surfaceSecondary : 'transparent',
    borderColor: selected || targeted ? theme.colors.secondary : 'transparent',
    borderCurve: 'continuous',
    borderRadius: theme.borderRadius.xl,
    borderStyle: targeted && !selected ? 'dotted' : 'solid',
    borderWidth: theme.size(0.5),
    marginRight: theme.space('md'),
    overflow: 'hidden',
    _web: {
      _hover: {
        backgroundColor: theme.colors.surfaceSecondary,
      },
    },
  }),
  teamRowContent: {
    paddingHorizontal: theme.space('lg'),
    minHeight: theme.size(13),
  },
  teamEdit: {
    alignItems: 'center',
  },
  teamStatic: {},
  teamSwapRail: {
    flexShrink: 0,
    paddingLeft: theme.space('md'),
  },
  teamText: (isEditing: boolean | undefined) => ({
    alignItems: isEditing ? 'flex-start' : undefined,
  }),
  timeBlock: {
    borderBottomColor: theme.colors.surfaceMuted,
    borderBottomRightRadius: theme.borderRadius.xl,
    borderBottomWidth: theme.size(0.5),
    borderRightColor: theme.colors.surfaceMuted,
    borderRightWidth: theme.size(0.5),
    minWidth: theme.size(30),
    padding: theme.space('sm'),
  },
  timeText: {
    fontWeight: theme.fontWeight.semibold,
  },
}));
